import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { getHideFields } from '../../lib/editFieldsConfig';

import FormFields from '../form/fields';
import FormFieldsCompact from '../form/fieldsCompact';
import Input from '../form/input';
import Versions from '../../components/versions';
import { appAlert, appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

@withText(props => ({
    orderNumber: <Text id='input.orderNumber-help'>orderNumber</Text>,
    name: <Text id='input.name-help'>name</Text>,
    description: <Text id='input.description-help'>description</Text>,
    installation: <Text id='input.installation-help'>installation</Text>,
    url: <Text id='input.url-help'>url</Text>,
    searchTitle: <Text id='input.search-for-visitor'>Search for visitor</Text>,

    mergeInfo: <Text id='dyrejournal.merge-info'>This is what is going to happend: 1. All animals are transferred to this visitor. 2. All sales are transferred to this visitor. 3. All journal references are transferred to this visitor. 4. Visitor you choose to merge is deleted.</Text>
}))
@observer
class NewVisitor extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('visitor'),
            missingFields: [],
            saveFailed: false,
        };
    }

    createVisitor = async () => {
        const { callback = () => {} } = this.props;
        const { visitorStore } = this.props.stores;
        const { newVisitor } = visitorStore;

        // Required fields (mirrors the visitor schema). Validate up front so a
        // missing field never wipes what the user has already typed.
        const missingFields = ['firstname', 'lastname'].filter(field => !newVisitor[field]);
        if (missingFields.length > 0) {
            this.setState({ missingFields, saveFailed: false });
            return;
        }

        const response = await visitorStore.insert(newVisitor);
        const visitor = response?.data?.visitor;
        if (response?.status === 201) {
            // Only clear the form once the visitor is actually saved.
            visitorStore.updateKeyValue('newVisitor', {});
            this.setState({ missingFields: [], saveFailed: false });
            callback(visitor);
        } else {
            // Keep the entered data so the user can fix it and try again.
            this.setState({ saveFailed: true });
        }
    }

    componentDidMount() {
        const { id, isNew } = this.props;
        const { visitorStore } = this.props.stores;
        visitorStore.loadVeterinaries();
        if (!isNew) {
            visitorStore.load(id);
        }
    }


    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ search });

        clearTimeout(this.searchTimer);
        if (search.length >= 3) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch(search);
            }, 300);
        }
    }

    async doSearch(search) {
        const { visitorStore } = this.props.stores;
        if (search) {
            // console.log('doUpdateField.saveField', id, field, value);
            const result = await visitorStore.searchVisitors({ search });
        } else {
            visitorStore.localUpdateField('searchResult', []);
        }
    }

    clearSearch = () => {
        this.setState({ search: '' });
        this.doSearch('');
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch(this.state.search);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.clearSearch();
        }
    }

    mergeVisitor = async e => {
        const dialogAction = beginDialogAction(this, 'mergeVisitor', () => [
            this.props?.stores?.visitorStore?.visitor?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { id } = e.target.closest('button').dataset;
            const { animalStore, visitorStore } = this.props.stores;
            const { animal } = animalStore;
            const { searchResult, visitor } = visitorStore;
            const mergeVisitor = searchResult.find(v => v.id === parseInt(id, 10));
            if (!((await appConfirm(`Merge current visitor with ${localUtil.displayName(mergeVisitor)}?`)) && dialogAction.isCurrent())) {
                return;
            }
            // console.log('Changing owner of animal to visitor:')
            // console.log({ visitor })
            const response = await visitorStore.mergeVisitors({
                currentVisitor: visitor.id,
                mergeWith: mergeVisitor.id,
            });

            const status = response && response.status;
            if (status !== 202 && status !== 200) {
                // Merge was rejected (e.g. one of the visitors is already merged /
                // deactivated). Do NOT show a false success confirmation.
                (await appAlert((response && response.message) || 'Could not merge the visitors. Please reload the page and try again.'));
                return;
            }

            this.setState({
                search: '',
                visitorMerged: true,
                mergeVisitor,
            });
            // await conventionStore.newParticipant(visitor);
            await this.loadAll();
        } finally {
            dialogAction.finish();
        }
    }

    renderFormFieldsCompact = () => {
        const { isNew, hideAdmin, drawerLevel } = this.props;
        const { sections } = this.state;
        const { visitorStore, userStore } = this.props.stores;
        const { customer = {} } = userStore;
        const hideFields = getHideFields(customer, 'visitor');

        return (
            <FormFieldsCompact
                stores={this.props.stores}
                sections={sections}
                holdingStore={visitorStore}
                objectName={isNew ? 'newVisitor' : 'visitor'}
                isNew={isNew}
                drawerLevel={drawerLevel}
                hideAdmin={hideAdmin}
                hideFields={hideFields}
            />
        );
    }

    openFieldsSettings = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('editFieldsSettings', {
            entityType: 'visitor',
            height: drawerHeightMediumLarge,
        }, drawerLevel + 1);
    }

    componentDidCatch(error, info) {
        console.error('NewVisitor componentDidCatch:', error);
        this.setState({ hasError: true, error, errorInfo: info });
    }

    render() {
        const {
            searchTitle,
            mergeInfo,
        } = this.props;
        const {
            search,
            visitorMerged,
            mergeVisitor,
            hasError,
            error,
            missingFields = [],
            saveFailed,
        } = this.state;
        const fieldLabels = {
            firstname: <Text id='input.firstname'>First name</Text>,
            lastname: <Text id='input.lastname'>Last name</Text>,
        };
        const { week, isNew, hideAdmin, drawerLevel } = this.props;
        const { sections } = this.state;
        const { userStore, visitorStore } = this.props.stores;
        const { user, isAdmin, isCustomerAdmin } = userStore;
        const { newVisitor, visitor, searchResult } = visitorStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        // DEBUG: Show error boundary message
        if (hasError) {
            return (
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100' style='max-width: 960px; padding: 20px;'>
                        <div class='alert alert-danger'>
                            <h5><i class='fa-solid fa-exclamation-triangle me-2' />FormFieldsCompact Error</h5>
                            <p><strong>Error:</strong> {error?.message || 'Unknown error'}</p>
                            <pre style='font-size: 11px; overflow: auto;'>{error?.stack}</pre>
                            <button class='btn btn-primary mt-2' onClick={() => this.setState({ hasError: false })}>
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <Versions stores={this.props.stores} type='visitor' id={visitor?.id} key={`visitor-${visitor.id}`} />

                    {/* <div class='w-100 d-flex flex-column justify-content-start'>
                        <xmp>{JSON.stringify(user.settings, null, 2)}</xmp>
                    </div> */}
                    {/* <xmp>{JSON.stringify(newVisitor, null, 4)}</xmp> */}
                    <div class='font-weight-lighter px-3 box-header mt-0 d-flex flex-row justify-content-between align-items-center'>
                        <span>
                            {isNew ? <Text id='visitor.new'>New visitor</Text> : <Text id='visitor.edit'>Edit visitor</Text>}
                        </span>
                        {/* me-5 keeps the cog clear of the drawer close X when the drawer is no wider than the content column. */}
                        {isCustomerAdmin && <button type='button' class='btn btn-sm btn-outline-secondary rounded-circle me-5'
                            onClick={this.openFieldsSettings}>
                            <i class='fa-solid fa-gear' />
                        </button>}
                    </div>
                    <div class='d-flex flex-column justify-content-start' style='padding: 0 8px;'>
                        {this.renderFormFieldsCompact()}
                    </div>
                    {isNew ? <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style={`bottom: 0; left: 0;`}>
                        {missingFields.length > 0 && <div class='alert alert-warning rounded-3 mb-2' role='alert'>
                            <i class='fa-duotone fa-circle-exclamation me-2' />
                            <Text id='visitor.missing-required'>Please fill in the required fields before saving — what you have already typed is kept.</Text>
                            {' '}
                            <strong>
                                {missingFields.map((field, idx) => (
                                    <span>{idx > 0 ? ', ' : ''}{fieldLabels[field] || field}</span>
                                ))}
                            </strong>
                        </div>}
                        {saveFailed && missingFields.length === 0 && <div class='alert alert-danger rounded-3 mb-2' role='alert'>
                            <i class='fa-duotone fa-triangle-exclamation me-2' />
                            <Text id='visitor.save-failed'>Could not save the visitor right now. Your entries are kept — please try again.</Text>
                        </div>}
                        <button type='button' class='btn btn-primary rounded-pill' onClick={this.createVisitor}>
                            {isNew ? <Text id='visitor.create'>Create visitor</Text> : <Text id='visitor.save'>Save visitor</Text>}
                        </button>
                    </div> : <>

                        <div class={`mt-5 mx-4 font-weight-lighter px-3 py-1 text-muted`}>
                            <small class='text-uppercase'>
                                <Text id='dyrejournal.merge-visitors'>Merge visitors</Text>
                            </small>
                        </div>
                        <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mx-3 d-flex flex-wrap py-3 px-3 mb-5 box-container`}>
                            {visitorMerged && mergeVisitor ? <>
                                <div class='d-flex flex-wrap justify-content-center w-100'>
                                    <div class='alert alert-success rounded-lg w-100'>
                                        <Text id='dyrejournal.visitor-merged'>Visitor merged with</Text>: {localUtil.displayName(mergeVisitor)}
                                    </div>
                                </div>
                            </> : <>
                                <div class='flex-fill input-group'>
                                    <div class='input-group-text rounded-pill-left'>
                                        <i class='fa-regular fa-magnifying-glass'></i>
                                    </div>
                                    <input
                                        class={`form-control ${!search ? 'rounded-pill-right' : ''}`}
                                        type='text'
                                        value={search}
                                        placeholder={`${searchTitle}`}
                                        onInput={this.searchInput}
                                        onKeyDown={this.handleKeyDown}
                                    />
                                    {search && <div class='input-group-text'>
                                        <button class='btn btn-danger btn-sm rounded-circle' type='button' onClick={this.clearSearch}>
                                            <i class='fa-solid fa-circle-xmark'></i>
                                        </button>
                                    </div>}
                                </div>
                                {searchResult && searchResult.length ? <>
                                    <div class='d-flex flex-wrap justify-content-center'>
                                        {searchResult.map(user => {
                                            const isCurrent = user.id === visitor.id;
                                            return (<>
                                                <button
                                                    disabled={isCurrent}
                                                    class={`btn btn-sm btn-outline-${isCurrent ? 'secondary text-strikethrough' : 'primary'} rounded-pill mx-1 my-1`}
                                                    onClick={this.mergeVisitor}
                                                    data-id={user.id}
                                                >
                                                    <i class={`fa-duotone fa-plus`} /> {localUtil.displayName(user)} {user.cellphone}
                                                </button>
                                            </>);
                                        })}
                                    </div>
                                </> : <></>}
                            </>}
                            <div class='d-flex flex-column'>
                                <small class='text-danger'>
                                    <i class='fa-duotone fa-exclamation-triangle me-2' style='font-size: 1.5em;' />
                                    <Text id='dyrejournal.merge-customer'>Search for customer you want to merge into this customer. This feature is Experimental! All execution is at your own risk!</Text>
                                </small>
                                <small class='text-muted'>
                                    <i class='fa-duotone fa-info-circle me-2' style='font-size: 1.5em;' /> {mergeInfo}
                                </small>
                            </div>
                        </div>
                    </>}

                </div>
            </div>
        </>);
    }
}

export default NewVisitor;
